import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Instagram, ArrowRight, Loader2 } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];
const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const INSTAGRAM_URL = "https://www.instagram.com/stories/themooncafeuk/";

export default function Notify() {
  const [email, setEmail] = useState("");
  const [state, setState] = useState("idle");
  const [message, setMessage] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (state === "sending") return;
    setState("sending");
    setMessage("");
    try {
      const res = await axios.post(`${API}/notify`, { email });
      setState("success");
      setMessage(res.data.message);
    } catch (err) {
      setState("error");
      const detail = err.response && err.response.data && err.response.data.detail;
      setMessage(
        typeof detail === "string"
          ? detail
          : "Something went wrong — please try again."
      );
    }
  };

  return (
    <section id="notify" className="border-t border-line/50 py-24 md:py-32">
      <div className="mx-auto max-w-3xl px-6 text-center md:px-12">
        <motion.p
          initial={{ opacity: 0, y: 14 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.8, ease: EASE }}
          className="font-sans text-xs uppercase tracking-[0.35em] text-sage"
        >
          Stay in the loop
        </motion.p>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.1 }}
          className="mt-5 font-serif text-4xl font-medium leading-tight text-cream sm:text-5xl md:text-6xl"
        >
          Be the first through the door.
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.2 }}
          className="mx-auto mt-6 max-w-md font-sans text-base font-light leading-relaxed text-cream-dim/80 md:text-lg"
        >
          Get one email when the new website launches — nothing else, promise.
        </motion.p>

        {state === "success" ? (
          <motion.p
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease: EASE }}
            data-testid="notify-success"
            className="mt-12 font-serif text-2xl italic text-sage md:text-3xl"
          >
            {message}
          </motion.p>
        ) : (
          <motion.form
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.15 }}
            transition={{ duration: 0.9, ease: EASE, delay: 0.25 }}
            onSubmit={submit}
            data-testid="notify-form"
            className="mx-auto mt-12 flex max-w-xl flex-col gap-4 sm:flex-row"
          >
            <label className="sr-only" htmlFor="notify-email">
              Email address
            </label>
            <input
              id="notify-email"
              data-testid="notify-email-input"
              type="email"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className="flex-1 rounded-full border border-line bg-transparent px-6 py-4 font-sans text-cream outline-none transition-colors duration-300 placeholder:text-sage-dim/60 focus:border-sage/60"
            />
            <button
              type="submit"
              data-testid="notify-submit"
              disabled={state === "sending"}
              className="inline-flex items-center justify-center gap-2 rounded-full bg-cream px-8 py-4 font-sans text-xs uppercase tracking-[0.25em] text-ink transition-colors duration-300 hover:bg-sage disabled:opacity-60"
            >
              {state === "sending" ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <>
                  Notify me
                  <ArrowRight className="h-3.5 w-3.5" />
                </>
              )}
            </button>
          </motion.form>
        )}
        {state === "error" && (
          <p data-testid="notify-error" className="mt-4 font-sans text-sm text-[#D9A0A0]">
            {message}
          </p>
        )}

        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.9, ease: EASE, delay: 0.35 }}
          className="mt-14"
        >
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="instagram-link"
            className="inline-flex items-center gap-3 font-sans text-xs uppercase tracking-[0.25em] text-sage transition-colors duration-300 hover:text-cream"
          >
            <Instagram className="h-4 w-4" />
            Follow us on Instagram
          </a>
        </motion.div>
      </div>
    </section>
  );
}
